/**
 * Utility functions to generate structured IDs for roadmaps and articles
 * 
 * ID HIERARCHY:
 * 
 * EXPERT/AI-GENERATED ROADMAPS:
 * - Roadmap ID: goal_intensity_language (e.g., "python_beginner_en", "python_beginner_bn")
 * - Article ID: roadmap_id_day_number (e.g., "python_beginner_en_01", "python_beginner_en_02")
 * - Different languages = different roadmaps (allows multi-language learning)
 * 
 * MANUAL/USER-CREATED ROADMAPS:
 * - Roadmap ID: roadmap_name_manual (e.g., "python-basics_manual", "web-dev_manual")
 * - Article ID: roadmap_id_day_number (e.g., "python-basics_manual_01", "python-basics_manual_02")
 * - Identified by "_manual" suffix for easy differentiation
 * 
 * Key Properties:
 * - Articles inherit roadmap ID and add sequential day number
 * - Manual roadmaps easily identifiable by "_manual" suffix
 * - Day number is zero-padded to 2 digits for sorting
 * - Day number is zero-padded to 2 digits for sorting
 */

/**
 * Normalize a string for use in IDs
 * Converts to lowercase, removes special chars, replaces spaces with hyphens
 */
function normalizeForId(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Remove duplicate hyphens
    .substring(0, 150); // Limit length
}

/**
 * Generate a short 4-character hash of a string
 * Used to prevent collisions between similar strings that normalize to the same value
 */
function simpleHash(text: string): string {
  let hash = 0;
  const str = text.toLowerCase().trim();
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  // Return base36 for compactness
  return Math.abs(hash).toString(36).substring(0, 4);
}

/**
 * Generate a structured roadmap ID
 * Format: goal_intensity_language
 * Example: "python_beginner_en", "python_beginner_bn"
 * Different languages = different roadmaps
 */
export function generateRoadmapId(
  goal: string,
  intensity: string,
  language: string
): string {
  const normalizedGoal = normalizeForId(goal);
  const normalizedIntensity = intensity.toLowerCase().replace(/\s+/g, '-');
  const normalizedLanguage = language.toLowerCase().replace(/\s+/g, '-');
  
  return `${normalizedGoal}_${normalizedIntensity}_${normalizedLanguage}`;
}

/**
 * Generate a manual roadmap ID (for user-created roadmaps)
 * Format: roadmap_name_manual
 * Example: "python-basics_manual", "web-development_manual"
 * Used for distinguishing user-created roadmaps from AI-generated ones
 */
export function generateManualRoadmapId(roadmapName: string): string {
  const normalized = normalizeForId(roadmapName);
  return `${normalized}_manual`;
}

/**
 * Generate a structured article ID
 * Format: roadmap_id_day_number (zero-padded to 2 digits)
 * Example: "python_beginner_en_01", "python_beginner_en_02", "python_beginner_bn_01"
 */
export function generateArticleId(
  roadmapId: string,
  dayNumber: number
): string {
  // Day number zero-padded to 2 digits
  const paddedDay = String(dayNumber).padStart(2, '0');
  
  return `${roadmapId}_${paddedDay}`;
}

/**
 * Generate a manual article ID (for articles in user-created roadmaps)
 * Format: roadmap_id_day_number
 * Example: "python-basics_manual_01", "python-basics_manual_02"
 * Used for articles in manual roadmaps
 */
export function generateManualArticleId(
  roadmapId: string,
  dayNumber: number
): string {
  // Day number zero-padded to 2 digits
  const paddedDay = String(dayNumber).padStart(2, '0');
  
  return `${roadmapId}_${paddedDay}`;
}

/**
 * Generate a cache key for articles (backward compatibility with localStorage)
 */
export function generateArticleCacheKey(
  roadmapId: string,
  dayId: string
): string {
  return `${roadmapId}_${dayId}`;
}

/**
 * Check if a roadmap is manual (user-created)
 * Manual roadmaps have the "_manual" suffix
 */
export function isManualRoadmap(roadmapId: string): boolean {
  return roadmapId.endsWith('_manual');
}

/**
 * Check if an article is from a manual roadmap
 * Based on the roadmap ID part of the article ID
 */
export function isManualArticle(articleId: string): boolean {
  // Article ID format: roadmap_id_day_number (or roadmap_id_XX for manual)
  // Extract roadmap ID by removing the last segment (day number)
  const parts = articleId.split('_');
  if (parts.length < 2) return false;
  
  // Remove the day number part (last 2 digits)
  const roadmapIdPart = parts.slice(0, -1).join('_');
  return isManualRoadmap(roadmapIdPart);
}

/**
 * Extract roadmap ID from an article ID
 */
export function extractRoadmapIdFromArticle(articleId: string): string {
  // Article ID format: roadmap_id_day_number
  // Day number is always the last segment (00-28 format for 28 days, etc)
  const parts = articleId.split('_');
  if (parts.length < 2) return articleId;
  
  // Remove the day number part (last 2 digits)
  return parts.slice(0, -1).join('_');
}

